import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState } from "@/components/common/StateBlocks";
import { profileService } from "@/services/profileService";
import { SPECIALTIES, BRAND_VOICES } from "@/config/formOptions";
import { FormSection, Field, ChipMultiSelect } from "@/components/profile/FormControls";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const EMPTY = {
  brand_name: "", brokerage: "", primary_market: "", markets_served: [], specialties: [],
  target_audience: "", brand_voice: [], custom_instructions: "", default_cta: "",
  business_phone: "", business_email: "", website: "", instagram: "", facebook: "",
  linkedin: "", tiktok: "", primary_color: "", secondary_color: "", logo_filename: "",
};

const fieldErrors = (err) => {
  const list =
    err?.details?.errors ||
    err?.error?.details?.errors ||
    err?.response?.data?.error?.details?.errors ||
    [];
  const map = {};
  list.forEach((e) => {
    const f = e.loc?.[e.loc.length - 1];
    if (f) map[f] = (e.msg || "").replace("Value error, ", "");
  });
  return map;
};

export default function BrandProfile() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [percent, setPercent] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    try {
      const [p, c] = await Promise.all([profileService.getBrand(), profileService.getCompleteness()]);
      if (p) {
        const clean = Object.fromEntries(
          Object.entries(p).map(([k, v]) => [k, v === null ? (Array.isArray(EMPTY[k]) ? [] : "") : v]),
        );
        setForm({ ...EMPTY, ...clean, markets_served: p.markets_served || [] });
      }
      setPercent(c.brand.percent);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await profileService.saveBrand({ ...form });
      const c = await profileService.getCompleteness();
      setPercent(c.brand.percent);
      toast.success("Brand profile saved");
    } catch (err) {
      setErrors(fieldErrors(err));
      toast.error(err.message || "Please correct the highlighted fields");
    } finally {
      setSaving(false);
    }
  };

  const showCustom = form.brand_voice.includes("custom");

  return (
    <AppShell>
      <div className="space-y-6" data-testid="brand-profile-page">
        <PageHeader
          eyebrow="AgentHQ · Market"
          title="Brand Profile"
          description="Your marketing brand identity. This context will power the Marketing Center and RESA in later phases."
          actions={percent != null && (
            <Badge variant="outline" className="border-accent/40 text-accent" data-testid="brand-completeness">{percent}% complete</Badge>
          )}
        />

        {loading ? (
          <div className="py-10"><LoadingState label="Loading brand profile" /></div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-6" data-testid="brand-profile-form" noValidate>
            <FormSection title="Brand Identity">
              <Field label="Public / professional brand name" htmlFor="brand_name" error={errors.brand_name}>
                <Input id="brand_name" value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} data-testid="brand-name" />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Brokerage" htmlFor="brokerage" error={errors.brokerage}>
                  <Input id="brokerage" value={form.brokerage} onChange={(e) => set("brokerage", e.target.value)} data-testid="brand-brokerage" />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Primary brand color (optional)" htmlFor="primary_color" error={errors.primary_color} hint="6-digit HEX, e.g. #0A1530">
                  <div className="flex items-center gap-2">
                    <input type="color" aria-label="Primary color" value={form.primary_color || "#0A1530"} onChange={(e) => set("primary_color", e.target.value.toUpperCase())} className="h-9 w-10 cursor-pointer rounded border border-border bg-transparent" />
                    <Input id="primary_color" value={form.primary_color} onChange={(e) => set("primary_color", e.target.value)} data-testid="brand-primary-color" placeholder="#0A1530" />
                  </div>
                </Field>
                <Field label="Secondary brand color (optional)" htmlFor="secondary_color" error={errors.secondary_color}>
                  <div className="flex items-center gap-2">
                    <input type="color" aria-label="Secondary color" value={form.secondary_color || "#D4AF37"} onChange={(e) => set("secondary_color", e.target.value.toUpperCase())} className="h-9 w-10 cursor-pointer rounded border border-border bg-transparent" />
                    <Input id="secondary_color" value={form.secondary_color} onChange={(e) => set("secondary_color", e.target.value)} data-testid="brand-secondary-color" placeholder="#D4AF37" />
                  </div>
                </Field>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5" /> Logo upload becomes available in the approved Documents / Storage phase.
              </div>
            </FormSection>

            <FormSection title="Audience">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Primary market" htmlFor="b_primary_market" error={errors.primary_market}>
                  <Input id="b_primary_market" value={form.primary_market} onChange={(e) => set("primary_market", e.target.value)} data-testid="brand-primary-market" placeholder="City, State" />
                </Field>
                <Field label="Markets served" htmlFor="markets" error={errors.markets_served} hint="Comma-separated">
                  <Input id="markets" value={form.markets_served.join(", ")} onChange={(e) => set("markets_served", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid="brand-markets" placeholder="Austin, Round Rock, Cedar Park" />
                </Field>
              </div>
              <Field label="Specialties" error={errors.specialties}>
                <ChipMultiSelect options={SPECIALTIES} value={form.specialties} onChange={(v) => set("specialties", v)} testid="brand-specialties" />
              </Field>
              <Field label="Ideal client / target audience" htmlFor="audience" error={errors.target_audience}>
                <Input id="audience" value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)} data-testid="brand-audience" placeholder="e.g. Move-up families and first-time luxury buyers" />
              </Field>
            </FormSection>

            <FormSection title="Brand Voice">
              <Field label="Brand voice" error={errors.brand_voice}>
                <ChipMultiSelect options={BRAND_VOICES} value={form.brand_voice} onChange={(v) => set("brand_voice", v)} testid="brand-voice" />
              </Field>
              {showCustom && (
                <Field label="Custom brand instructions" htmlFor="custom_instructions" error={errors.custom_instructions}>
                  <Textarea id="custom_instructions" rows={4} value={form.custom_instructions} onChange={(e) => set("custom_instructions", e.target.value)} data-testid="brand-custom" placeholder="Describe your voice in your own words…" />
                </Field>
              )}
            </FormSection>

            <FormSection title="Default Messaging">
              <Field label="Default call-to-action" htmlFor="cta" error={errors.default_cta}>
                <Input id="cta" value={form.default_cta} onChange={(e) => set("default_cta", e.target.value)} data-testid="brand-cta" placeholder="e.g. Book a free home valuation" />
              </Field>
            </FormSection>

            <FormSection title="Online Presence">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Business phone (optional)" htmlFor="phone" error={errors.business_phone}>
                  <Input id="phone" value={form.business_phone} onChange={(e) => set("business_phone", e.target.value)} data-testid="brand-phone" />
                </Field>
                <Field label="Business email (optional)" htmlFor="bemail" error={errors.business_email}>
                  <Input id="bemail" value={form.business_email} onChange={(e) => set("business_email", e.target.value)} data-testid="brand-email" />
                </Field>
              </div>
              <Field label="Website (optional)" htmlFor="bwebsite" error={errors.website}>
                <Input id="bwebsite" value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="brand-website" />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Instagram (optional)" htmlFor="ig" error={errors.instagram}>
                  <Input id="ig" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} data-testid="brand-instagram" placeholder="@handle" />
                </Field>
                <Field label="TikTok (optional)" htmlFor="tt" error={errors.tiktok}>
                  <Input id="tt" value={form.tiktok} onChange={(e) => set("tiktok", e.target.value)} data-testid="brand-tiktok" placeholder="@handle" />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Facebook (optional)" htmlFor="fb" error={errors.facebook}>
                  <Input id="fb" value={form.facebook} onChange={(e) => set("facebook", e.target.value)} data-testid="brand-facebook" />
                </Field>
                <Field label="LinkedIn (optional)" htmlFor="li" error={errors.linkedin}>
                  <Input id="li" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} data-testid="brand-linkedin" />
                </Field>
              </div>
            </FormSection>

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={saving} data-testid="brand-save">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><CheckCircle2 className="h-4 w-4" /> Save brand profile</>}
              </Button>
            </div>
          </form>
        )}
      </div>
    </AppShell>
  );
}
